import { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

const shimmerStyle = {
  background: 'linear-gradient(90deg, #f0f2f7 25%, #e2e6ef 50%, #f0f2f7 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite',
  borderRadius: '4px',
};

const LineChart = ({
  data,
  loading = false,
  y1Label = 'Avg CPU %',
  y2Label = 'Monthly Cost ($)',
  y1Suffix = '%',
  y2Prefix = '$',
  chartType = 'bar',
  y2BeginAtZero = true,
  xMaxRotation = 35,
  y1Max = 100,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (loading || !data || !canvasRef.current) return;

    const existing = Chart.getChart(canvasRef.current);
    if (existing) existing.destroy();

    const chart = new Chart(canvasRef.current, {
      type: chartType,
      data: data,
      options: {
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { font: { size: 11 }, boxWidth: 10, padding: 12 },
          },
        },
        scales: {
          y1: {
            position: 'left',
            beginAtZero: true,
            ...(y1Max != null ? { max: y1Max } : {}),
            ticks: {
              font: { size: 10 },
              callback: (v) => v + y1Suffix,
            },
            grid: { color: '#eef1f7' },
            title: {
              display: !!y1Label,
              text: y1Label,
              font: { size: 10 },
              color: '#9aa3b7',
            },
          },
          y2: {
            position: 'right',
            beginAtZero: y2BeginAtZero,
            ticks: {
              font: { size: 10 },
              callback: (v) => y2Prefix + v,
            },
            grid: { display: false },
            title: {
              display: !!y2Label,
              text: y2Label,
              font: { size: 10 },
              color: '#9aa3b7',
            },
          },
          x: {
            ticks: {
              font: { size: 9 },
              autoSkip: true,
              maxRotation: xMaxRotation,
              callback: function(val, index) {
                const label = this.getLabelForValue(val);
                return label.length > 12 ? label.slice(0, 12) + '…' : label;
              },
            },
            grid: { display: false },
          },
        },
      },
    });

    return () => chart.destroy();
  }, [data, loading, chartType, y1Label, y2Label, y1Suffix, y2Prefix, y2BeginAtZero, xMaxRotation, y1Max]);

  if (loading) {
    return (
      <div className="chart-wrap" style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', padding: '10px 0' }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <div style={{ ...shimmerStyle, width: '100%', height: `${[60, 80, 45, 90, 55, 70, 40, 75, 50, 65][i]}%` }} />
            <div style={{ ...shimmerStyle, width: '80%', height: '8px' }} />
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="chart-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: '12px' }}>
        No data available
      </div>
    );
  }

  return (
    <div className="chart-wrap">
      <canvas ref={canvasRef}></canvas>
    </div>
  );
};

export default LineChart;